import { FC, useEffect, useState, useCallback } from "react";
import { Loading } from "../../components/Loading";
import "./index.css";
import Swal from "sweetalert2";
import { useAppConfig } from "../../hooks/useAppConfig";
import { ConfigType } from "../../contexts/AppConfigContext";
import { Menu } from "../../components/Menu";
import { Header } from "../../components/Header";
import { useSeller } from "../../hooks/useSeller";
import { SellerQueueCard } from "../../components/SellerQueueCard";
import { useSellerQueue } from "../../hooks/useSellerQueue";
import { useSellerSession, NoSaleFormData } from "../../hooks/useSellerSession";
import { useSellerQueueRealtime, SellerQueueRealtimeEvent } from "../../hooks/useSellerQueueRealtime";
import { AttendanceModal } from "../../components/AttendanceModal";
import { useSellerQueueContext } from "../../contexts/SellerQueueContext";

/**
 * SellerAttendance Page
 * Queue management interface for sellers with Card, Modal, and Real-time updates
 */
const SellerAttendance: FC = () => {
  const { getSellerAppConfig } = useAppConfig();
  const { getTokenSeller } = useSeller();
  const [loadingHeader, setLoadingHeader] = useState(true);
  const [config, setConfig] = useState<ConfigType | void>();

  // Queue state and actions
  const queueHook = useSellerQueue();
  const { queueEntry, isLoading: isLoadingQueue, error: errorQueue, refetchQueue } = queueHook;
  const { enterQueue, pauseQueue, resumeQueue, leaveQueue } = queueHook;

  // Initial load - check if seller is already in queue
  useEffect(() => {
    refetchQueue();
  }, [refetchQueue]);

  // Session state and actions
  const sessionHook = useSellerSession();
  const { currentSession, endService, startService, getElapsedSeconds } = sessionHook;

  // Context for updating real-time
  const { setQueueEntry } = useSellerQueueContext();

  // Local UI state
  const [showEndServiceModal, setShowEndServiceModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const showNotification = (type: "success" | "error", message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  // Real-time updates via WebSocket (wrapped in useCallback to prevent infinite loop)
  const handleRealtimeEvent = useCallback((event: SellerQueueRealtimeEvent) => {
    console.log("[SellerAttendance] Real-time event:", event);

    if (event.type === "called") {
      showNotification("success", "📞 Você foi chamado! Clique em 'Iniciar Atendimento'");
    } else if (event.type === "position-updated") {
      if (event.data && queueEntry) {
        setQueueEntry({
          ...queueEntry,
          position: event.data.position,
        });
      }
    } else if (event.type === "status-changed") {
      if (event.data && queueEntry) {
        setQueueEntry({
          ...queueEntry,
          status: event.data.status,
        });
      }
    }
  }, [queueEntry, setQueueEntry]);

  useSellerQueueRealtime(handleRealtimeEvent);

  // Update elapsed time every second during service
  useEffect(() => {
    if (!currentSession || currentSession.status !== "in_progress") {
      setElapsedTime(0);
      return;
    }

    const interval = setInterval(() => {
      setElapsedTime(getElapsedSeconds());
    }, 1000);

    return () => clearInterval(interval);
  }, [currentSession, getElapsedSeconds]);

  // Load config
  const getConfig = useCallback(async () => {
    try {
      setLoadingHeader(true);
      const sellerConfig = await getSellerAppConfig();
      setConfig(sellerConfig);
      setLoadingHeader(false);
    } catch (error) {
      console.error("Error loading seller config:", error);
      setLoadingHeader(false);
    }
  }, [getSellerAppConfig]);

  useEffect(() => {
    getConfig();
  }, [getConfig]);

  // Handlers
  const handleEnterQueue = async () => {
    try {
      setIsProcessing(true);
      await enterQueue();
      showNotification("success", "✓ Você entrou na fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao entrar na fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePauseQueue = async () => {
    try {
      setIsProcessing(true);
      await pauseQueue();
      showNotification("success", "⏸ Você está pausado");
    } catch (error) {
      showNotification("error", `Erro ao pausar: ${error instanceof Error ? error.message : "Erro desconhecido"}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResumeQueue = async () => {
    try {
      setIsProcessing(true);
      await resumeQueue();
      showNotification("success", "▶ Você retomou o atendimento");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao retomar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeaveQueue = async () => {
    const result = await Swal.fire({
      title: "Sair da Fila",
      text: "Tem certeza que deseja sair da fila de atendimento?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, sair",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setIsProcessing(true);
      await leaveQueue();
      showNotification("success", "✓ Você saiu da fila");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao sair da fila: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStartService = async () => {
    if (!queueEntry?.id) return;

    try {
      setIsProcessing(true);
      await startService(queueEntry.id);
      showNotification("success", "🎯 Atendimento iniciado!");
    } catch (error) {
      showNotification(
        "error",
        `Erro ao iniciar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEndServiceClick = () => {
    setShowEndServiceModal(true);
  };

  const handleOutcomeSelected = async (outcome: string) => {
    try {
      setIsProcessing(true);
      await endService(outcome);
      showNotification("success", "✓ Atendimento encerrado");
      setShowEndServiceModal(false);
    } catch (error) {
      showNotification(
        "error",
        `Erro ao encerrar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNoSaleSubmit = async (data: NoSaleFormData) => {
    try {
      setIsProcessing(true);
      await endService("no_sale", data);
      showNotification("success", "✓ Atendimento encerrado");
      setShowEndServiceModal(false);
    } catch (error) {
      showNotification(
        "error",
        `Erro ao salvar: ${error instanceof Error ? error.message : "Erro desconhecido"}`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const tokenSeller = getTokenSeller();
  if (!tokenSeller) {
    return (
      <div className="sellerAttendanceHome">
        <div className="content-attendance">
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <p style={{ color: "#999", fontSize: "1em" }}>Erro: Vendedor não autenticado</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sellerAttendanceHome">
      <div className="content-attendance">
        {!loadingHeader && (
          <Header
            background={
              config?.webappcolor.backgroundheader ? config?.webappcolor.backgroundheader : "#F5F5F5"
            }
            colorIcon={config?.webappcolor.iconsheader ? config.webappcolor.iconsheader : "#636363"}
            logo={config?.icon ? config.icon : ""}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config?.content && config?.content.pdv && config?.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
            extraSpacingLeftMenu
          />
        )}

        {loadingHeader ? (
          <div style={{ width: "100%", height: "calc(100vh)", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
          </div>
        ) : (
          <div className="attendance-values">
            {/* Notifications */}
            {notification && (
              <div className={`notification notification-${notification.type}`}>
                {notification.message}
              </div>
            )}

            {/* Page Header */}
            <div className="page-header">
              <h1>Fila de Atendimento</h1>
              {currentSession?.status === "in_progress" && <div className="timer-display">⏱ {formatTime(elapsedTime)}</div>}
            </div>

            {/* Main Card */}
            <SellerQueueCard
              queueEntry={queueEntry}
              isLoading={isLoadingQueue}
              error={errorQueue}
              onEnterQueue={handleEnterQueue}
              onPause={handlePauseQueue}
              onResume={handleResumeQueue}
              onLeaveQueue={handleLeaveQueue}
              onStartService={handleStartService}
              onEndService={handleEndServiceClick}
              isServiceActive={currentSession?.status === "in_progress"}
              isPauseLoading={isProcessing}
              isResumeLoading={isProcessing}
              isLeaveLoading={isProcessing}
              isStartServiceLoading={isProcessing}
            />

            {/* Current Service Info */}
            {currentSession && currentSession.status === "in_progress" && (
              <div className="service-info-panel">
                <h3>Atendimento em Progresso</h3>
                <div className="info-details">
                  <div className="info-row">
                    <span className="info-label">Duração:</span>
                    <span className="info-value">{formatTime(elapsedTime)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Posição ao Iniciar:</span>
                    <span className="info-value">#{currentSession.queuePositionAtStart}</span>
                  </div>
                </div>
              </div>
            )}

            {/* End Service Modal */}
            <AttendanceModal
              isOpen={showEndServiceModal}
              isLoading={isProcessing}
              onClose={() => setShowEndServiceModal(false)}
              onOutcomeSelected={handleOutcomeSelected}
              onNoSaleSubmit={handleNoSaleSubmit}
            />
          </div>
        )}

        {!loadingHeader && (
          <Menu
            activeMenu="attendance"
            fontColor={config?.webappcolor.iconsfooter ? config?.webappcolor.iconsfooter : "#636363"}
            activeFontColor={config?.webappcolor.highlightfooter ? config?.webappcolor.highlightfooter : "#000"}
            backgroundMenu={config?.webappcolor.backgroundfooter ? config?.webappcolor.backgroundfooter : "#F5F5F5"}
            colorIcons={config?.webappcolor.iconsfooter ? config?.webappcolor.iconsfooter : "#636363"}
            highlightColorIcons={config?.webappcolor.highlightfooter ? config?.webappcolor.highlightfooter : "#636363"}
            backgroundCart={config?.webappcolor.backgroundcart ? config.webappcolor.backgroundcart : "#FFBA49"}
            fontCart={config?.webappcolor.fontcart ? config.webappcolor.fontcart : "#000"}
            noSales={
              config && config?.content && config?.content.pdv && config?.content.pdv.noSales
                ? config.content.pdv.noSales
                : false
            }
          />
        )}
      </div>
    </div>
  );
};

export { SellerAttendance };
